import { useTheme } from '@mui/material';
import { type Driver, type DriveStep, driver } from 'driver.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { WalkthroughMixpanelSection } from '/@/store/tracking/events/walkthrough';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import useIsPlaywriteEnvironment from '/@/utils/hooks/is-playwrite-environment';
import { reverse } from '/@/utils/router';
import { renderPopover } from '.';

interface UseDriverOptions {
    storageKey: string;
    steps: DriveStep[];
    onComplete?: () => void;
    onSkip?: () => void;
    enabled?: boolean;
    /** Shown as the small chip above the step counter in the custom popover. */
    popoverTag?: string;
    /** When set, emits `walkthrough_*` Mixpanel events instead of legacy `rv-onboarding`. */
    mixpanelSection?: WalkthroughMixpanelSection;
    /** Show “Ver más en el centro de ayuda” (route always registered; sidebar may still hide Help). */
    showHelpCenterLink?: boolean;
}

export const useDriver = ({
    storageKey,
    steps,
    enabled = true,
    popoverTag,
    mixpanelSection,
    showHelpCenterLink,
}: UseDriverOptions) => {
    const popoverTagRef = useRef(popoverTag);
    popoverTagRef.current = popoverTag;
    const mixpanelSectionRef = useRef(mixpanelSection);
    mixpanelSectionRef.current = mixpanelSection;
    const showHelpCenterLinkRef = useRef(showHelpCenterLink);
    showHelpCenterLinkRef.current = showHelpCenterLink;
    const completedViaFinalizarRef = useRef(false);

    const handleLastStepPrimary = useCallback(() => {
        const s = mixpanelSectionRef.current;
        if (!s) return;
        completedViaFinalizarRef.current = true;
        mpTrackEvent('walkthrough_completed', { section: s });
    }, []);
    const driverObjRef = useRef<Driver | null>(null);
    const [driverInitialized, setDriverInitialized] = useState(false);
    const [completed, setCompleted] = useState(false);

    // Check if onboarding was already completed
    useEffect(() => {
        const stored = localStorage.getItem(storageKey);
        setCompleted(stored === 'true');
    }, [storageKey]);

    const theme = useTheme();
    const navigate = useNavigate();
    const isPlaywriteEnvironment = useIsPlaywriteEnvironment();

    // Initialize driver only once
    useEffect(() => {
        if (!driverInitialized && !completed && enabled && !isPlaywriteEnvironment && steps.length > 0) {
            driverObjRef.current = driver({
                showProgress: true,
                stagePadding: 4,
                smoothScroll: true,
                steps: steps.map((step) => ({
                    ...step,
                    popover: {
                        ...step.popover,
                        title: ' ',
                        description: ' ',
                        onPopoverRender: (popover: any, opts: any) => {
                            renderPopover(
                                opts,
                                step.popover?.title || ' ',
                                step.popover?.description || ' ',
                                theme,
                                steps.length,
                                popoverTagRef.current,
                                undefined,
                                undefined,
                                mixpanelSectionRef.current ? handleLastStepPrimary : undefined,
                                showHelpCenterLinkRef.current
                                    ? () => {
                                          opts.driver.destroy();
                                          navigate(reverse('helpCenter.Home'));
                                      }
                                    : undefined
                            );
                        },
                    },
                })),
                allowClose: true,
                overlayClickBehavior: 'close',
                disableActiveInteraction: true,
                animate: true,
                onDestroyed: (element, step, opts) => {
                    const mp = mixpanelSectionRef.current;
                    if (mp) {
                        if (!completedViaFinalizarRef.current && !opts.driver.isLastStep()) {
                            mpTrackEvent('walkthrough_dismissed', { section: mp });
                        }
                        completedViaFinalizarRef.current = false;
                    } else if (opts.driver.isLastStep()) {
                        mpTrackEvent('rv-onboarding', {
                            action: 'completed',
                        });
                    } else {
                        mpTrackEvent('rv-onboarding', {
                            action: 'skipped',
                        });
                    }
                    localStorage.setItem(storageKey, 'true');
                    setCompleted(true);
                },
                onCloseClick: (element, step, opts) => {
                    localStorage.setItem(storageKey, 'true');
                    if (!mixpanelSectionRef.current) {
                        mpTrackEvent('rv-onboarding', {
                            action: 'skipped',
                        });
                    }
                    setCompleted(true);
                    opts.driver.destroy();
                },
            });
            setDriverInitialized(true);
        }
    }, [
        driverInitialized,
        completed,
        enabled,
        handleLastStepPrimary,
        isPlaywriteEnvironment,
        navigate,
        steps,
        storageKey,
        theme,
    ]);

    const startDriver = useCallback(() => {
        if (driverObjRef.current && !completed) {
            completedViaFinalizarRef.current = false;
            if (mixpanelSectionRef.current) {
                mpTrackEvent('walkthrough_started', { section: mixpanelSectionRef.current });
            }
            driverObjRef.current.drive();
        }
    }, [completed]);

    const destroyDriver = () => {
        if (driverObjRef.current) {
            driverObjRef.current.destroy();
        }
    };

    return {
        driverInitialized,
        completed,
        startDriver,
        destroyDriver,
        driverRef: driverObjRef.current,
    };
};

export type UseManualDriverOptions = {
    /** Shown as the small chip above the step counter in the custom popover. */
    popoverTag?: string;
    mixpanelSection?: WalkthroughMixpanelSection;
    showHelpCenterLink?: boolean;
};

/** On-demand tour (e.g. help icon): no localStorage, can be started any time. */
export const useManualDriver = (steps: DriveStep[], options?: UseManualDriverOptions) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const driverObjRef = useRef<Driver | null>(null);
    const stepsRef = useRef(steps);
    stepsRef.current = steps;
    const popoverTagRef = useRef(options?.popoverTag);
    popoverTagRef.current = options?.popoverTag;
    const mixpanelSectionRef = useRef(options?.mixpanelSection);
    mixpanelSectionRef.current = options?.mixpanelSection;
    const showHelpCenterLinkRef = useRef(options?.showHelpCenterLink);
    showHelpCenterLinkRef.current = options?.showHelpCenterLink;
    const completedViaFinalizarRef = useRef(false);
    const isPlaywriteEnvironment = useIsPlaywriteEnvironment();

    const handleLastStepPrimary = useCallback(() => {
        const s = mixpanelSectionRef.current;
        if (!s) return;
        completedViaFinalizarRef.current = true;
        mpTrackEvent('walkthrough_completed', { section: s });
    }, []);

    const startTour = useCallback(() => {
        const currentSteps = stepsRef.current;
        if (isPlaywriteEnvironment || currentSteps.length === 0) return;

        driverObjRef.current?.destroy();

        completedViaFinalizarRef.current = false;
        if (mixpanelSectionRef.current) {
            mpTrackEvent('walkthrough_started', { section: mixpanelSectionRef.current });
        }

        driverObjRef.current = driver({
            showProgress: true,
            stagePadding: 4,
            smoothScroll: true,
            steps: currentSteps.map((step) => ({
                ...step,
                popover: {
                    ...step.popover,
                    title: ' ',
                    description: ' ',
                    onPopoverRender: (popover: any, opts: any) => {
                        renderPopover(
                            opts,
                            step.popover?.title || ' ',
                            step.popover?.description || ' ',
                            theme,
                            currentSteps.length,
                            popoverTagRef.current,
                            undefined,
                            undefined,
                            mixpanelSectionRef.current ? handleLastStepPrimary : undefined,
                            showHelpCenterLinkRef.current
                                ? () => {
                                      opts.driver.destroy();
                                      navigate(reverse('helpCenter.Home'));
                                  }
                                : undefined
                        );
                    },
                },
            })),
            allowClose: true,
            overlayClickBehavior: 'close',
            disableActiveInteraction: true,
            animate: true,
            onDestroyed: (_element, _step, opts) => {
                const mp = mixpanelSectionRef.current;
                if (mp && !completedViaFinalizarRef.current && !opts.driver.isLastStep()) {
                    mpTrackEvent('walkthrough_dismissed', { section: mp });
                }
                completedViaFinalizarRef.current = false;
                driverObjRef.current = null;
            },
            onCloseClick: (_element, _step, opts) => {
                opts.driver.destroy();
            },
        });
        driverObjRef.current.drive();
    }, [handleLastStepPrimary, isPlaywriteEnvironment, navigate, theme]);

    useEffect(() => {
        return () => {
            driverObjRef.current?.destroy();
            driverObjRef.current = null;
        };
    }, []);

    return { startTour };
};
